import type { JssStyle } from 'jss';
import type { FormWidgetData } from '@web/widgets/form/type';

import { themeColorCss, themeFontCss } from '@sites/site-theme/src/utils/variables';
import { ThemeFontTags } from '@sites/site-theme/src/enums/font-tags.enum';
import { ThemeFontProperties } from '@sites/site-theme/src/enums/font-properties.enum';
import { ThemeColorNames } from '@sites/site-theme/src/enums/color-names.enum';
import {
    EButtonStyleType,
    EButtonType,
    EButtonWidthType,
    EHorizontalPosition,
    ESize,
    FormStyleEnum,
    InputBorderEnum,
    Widgets,
} from '@common/enums';

import {
    commonLabelStyles,
    commonWebsitesFormInputStyles,
    commonWebsitesFormTextareaStyles,
} from '@common/constants/input-sizes.const';
import { commonWebsitesFormButtonTextStyles } from '@common/constants/button-sizes.const';

import { getBaseButtonData, getBaseButtonIconStyles } from '@web/widgets/common/base/base-button/base-button.styles';
import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';
import { defaultInputSettingsMap } from '@web/widgets/common/base/base-form/defaults';
import { type PaymentFormWidgetData } from '@web/widgets/payment-form/type';
import { type LoginEduWidgetData } from '@web/widgets/login-edu/type';
import type { ShopCartWidgetData } from '@web/widgets/shop-cart/type';
import type { ThemedBaseButton } from '@web/widgets/common/base/base-button/base-button.type';
import type { ChatBotsButtonSettings } from '@web/widgets/form/type/form-widget.type';

const horizontalPositionMap = {
    [EHorizontalPosition.L]: 'flex-start',
    [EHorizontalPosition.M]: 'center',
    [EHorizontalPosition.R]: 'flex-end',
};

const inputSizeMap = {
    [ESize.S]: '11px',
    [ESize.M]: '13px',
    [ESize.L]: '19px',
};

const passwordIconMap = {
    [ESize.S]: 14,
    [ESize.M]: 16,
    [ESize.L]: 20,
};

function getChatBotIconButtonSizeStyles(Size: ESize): JssStyle {
    return {
        padding: (Size === ESize.S ? '8px 12px' : Size === ESize.M ? '12px 16px' : '12px 16px') + ' !important',
        gap: Size === ESize.S ? '2px' : Size === ESize.M ? '4px' : '6px',
        '& .icon': {
            width: (Size === ESize.S ? '18px' : Size === ESize.M ? '24px' : '32px') + ' !important',
            height: (Size === ESize.S ? '18px' : Size === ESize.M ? '24px' : '32px') + ' !important',
            '& svg': {
                width: (Size === ESize.S ? '18px' : Size === ESize.M ? '24px' : '32px') + ' !important',
                height: (Size === ESize.S ? '18px' : Size === ESize.M ? '24px' : '32px') + ' !important',
            },
        },
        '& span': {
            fontSize: (Size === ESize.S ? '10px' : Size === ESize.M ? '12px' : '14px') + ' !important',
            lineHeight: (Size === ESize.S ? '14px' : Size === ESize.M ? '17px' : '20px') + ' !important',
            fontWeight: 'normal',
        },
    };
}

function getButtonStyles(
    {
        buttonBase,
        buttonHover,
        styleType,
        advancedRadius,
        advancedBorderSettings,
    }: ThemedBaseButton & ChatBotsButtonSettings,
    fontSize: number
): JssStyle {
    if (styleType === EButtonStyleType.custom) {
        return {
            ...getBaseButtonData({ buttonBase, buttonHover, advancedRadius, advancedBorderSettings }),
            ...getBaseButtonIconStyles(fontSize),
        };
    }

    return {
        ...getBaseButtonIconStyles(fontSize),
    };
}

export function getBaseFormStyles(
    formData: FormWidgetData | PaymentFormWidgetData | LoginEduWidgetData | ShopCartWidgetData,
    widget: Widgets = Widgets.FORM,
    isConstructor?: boolean,
    isSubscriptionForm: boolean = false
): JssStyle {
    const inputSettings = formData.inputSettings ?? defaultInputSettingsMap[widget];
    const formStyleType = inputSettings.formStyleType ?? FormStyleEnum.VERTICAL;
    const formButtonSettings = 'formButtonSettings' in formData ? formData.formButtonSettings : null;
    const modalFormButtonSettings = 'modalFormButtonSettings' in formData ? formData.modalFormButtonSettings : null;
    const chatBotsButtonSettings = 'chatBotsButtonSettings' in formData ? formData.chatBotsButtonSettings : null;

    const formControl = {
        ...commonWebsitesFormInputStyles[inputSettings?.inputSize],
        '&:hover': {
            borderColor: '#9a9a9a',
        },
        '&:focus': {
            borderColor: '#82f6ff',
            boxShadow: '0 0 0 2px rgb(151 247 255 / 30%);',
            outline: '0',
        },
        borderWidth: '1px',
        boxSizing: 'border-box',
        borderStyle: 'solid',
        whiteSpace: 'nowrap',
        borderTopWidth: `${inputSettings.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
        borderRightWidth: `${inputSettings.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
        borderLeftWidth: `${inputSettings.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
        borderRadius: `${inputSettings.borderRadius}px`,
        background: themeColorCss(inputSettings.backgroundColor as string),
        borderColor: themeColorCss(inputSettings.borderColor as string),
        color: themeColorCss(inputSettings.inputTextColor as string),
        width: '100%',
        '& option': {
            background: themeColorCss(inputSettings.backgroundColor as string),
        },
        '&::placeholder': {
            color: themeColorCss(inputSettings.inputTextColor as string),
            opacity: 0.5,
        },
        '&.ng-select': {
            '& .ng-select-container': {
                minHeight: 'auto',
                height: 'auto',
                border: 'none',
                background: 'transparent',
                '& .ng-value-container': {
                    margin: '-1px',
                    padding: '0',
                    '& .ng-placeholder': {
                        top: 0,
                        color: themeColorCss(inputSettings.inputTextColor as string),
                        opacity: 0.5,
                    },
                    '& .ng-value': {
                        margin: '2px !important',
                        color: themeColorCss(inputSettings.inputTextColor as string),
                    },
                    '& .ng-input': {
                        display: 'none',
                    },
                },
                '&:hover': {
                    boxShadow: 'none !important',
                },
                '& .ng-clear-wrapper': {
                    color: themeColorCss(inputSettings.inputTextColor as string),
                    '&:hover': {
                        opacity: 0.6,
                    },
                },
                '& .ng-arrow-wrapper': {
                    marginRight: '-10px',
                    '& .ng-arrow': {
                        borderTopColor: themeColorCss(inputSettings.inputTextColor as string),
                    },
                    '&:hover': {
                        opacity: 0.6,
                    },
                },
            },
            '&.ng-select-opened': {
                '& .ng-select-container': {
                    '& .ng-arrow-wrapper': {
                        '& .ng-arrow': {
                            borderBottomColor: themeColorCss(inputSettings.inputTextColor as string),
                        },
                    },
                },
            },
        },
    };

    const formControlTextarea = {
        ...commonWebsitesFormTextareaStyles[inputSettings?.inputSize],
        '&:hover': {
            borderColor: '#9a9a9a',
        },
        '&:focus': {
            borderColor: '#82f6ff',
            boxShadow: '0 0 0 2px rgb(151 247 255 / 30%);',
            outline: '0',
        },
        borderWidth: '1px',
        boxSizing: 'border-box',
        borderStyle: 'solid',
        borderTopWidth: `${inputSettings.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
        borderRightWidth: `${inputSettings.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
        borderLeftWidth: `${inputSettings.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
        borderRadius: `${inputSettings.borderRadius}px`,
        background: themeColorCss(inputSettings.backgroundColor as string),
        borderColor: themeColorCss(inputSettings.borderColor as string),
        color: themeColorCss(inputSettings.inputTextColor as string),
        width: '100%',
        '& option': {
            background: themeColorCss(inputSettings.backgroundColor as string),
        },
    };

    let modalFormControl = {};
    let modalFormControlTextarea = {};

    if (widget === Widgets.FORM) {
        modalFormControl = {
            ...commonWebsitesFormInputStyles[inputSettings?.modalInputSize ? inputSettings.modalInputSize : ESize.M],
            '&:hover': {
                borderColor: '#9a9a9a',
            },
            '&:focus': {
                borderColor: '#82f6ff',
                boxShadow: '0 0 0 2px rgb(151 247 255 / 30%);',
                outline: '0',
            },
            borderWidth: '1px',
            boxSizing: 'border-box',
            borderStyle: 'solid',
            whiteSpace: 'nowrap',
            borderTopWidth: `${inputSettings?.modalInputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
            borderRightWidth: `${inputSettings?.modalInputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
            borderLeftWidth: `${inputSettings?.modalInputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
            borderRadius: `${inputSettings?.modalInputBorderRadius}px`,
            background: themeColorCss(inputSettings?.modalInputBackgroundColor as string),
            borderColor: themeColorCss(inputSettings?.modalInputBorderColor as string),
            color: themeColorCss(inputSettings?.modalInputTextColor as string),
            width: '100%',
            '& option': {
                background: themeColorCss(
                    inputSettings?.modalInputBackgroundColor
                        ? inputSettings?.modalInputBackgroundColor
                        : ('transparent' as string)
                ),
            },
            '&::placeholder': {
                color: themeColorCss(inputSettings?.modalInputTextColor as string),
                opacity: 0.5,
            },
            '&.ng-select': {
                '& .ng-select-container': {
                    minHeight: 'auto',
                    height: 'auto',
                    border: 'none',
                    background: 'transparent',
                    '& .ng-value-container': {
                        margin: '-1px',
                        padding: '0',
                        '& .ng-placeholder': {
                            top: 0,
                            color: themeColorCss(inputSettings?.modalInputTextColor as string),
                            opacity: 0.5,
                        },
                        '& .ng-value': {
                            margin: '2px !important',
                            color: themeColorCss(inputSettings?.modalInputTextColor as string),
                        },
                        '& .ng-input': {
                            display: 'none',
                        },
                    },
                    '&:hover': {
                        boxShadow: 'none !important',
                    },
                    '& .ng-clear-wrapper': {
                        color: themeColorCss(inputSettings?.modalInputTextColor as string),
                        '&:hover': {
                            opacity: 0.6,
                        },
                    },
                    '& .ng-arrow-wrapper': {
                        marginRight: '-10px',
                        '& .ng-arrow': {
                            borderTopColor: themeColorCss(inputSettings?.modalInputTextColor as string),
                        },
                        '&:hover': {
                            opacity: 0.6,
                        },
                    },
                },
                '&.ng-select-opened': {
                    '& .ng-select-container': {
                        '& .ng-arrow-wrapper': {
                            '& .ng-arrow': {
                                borderBottomColor: themeColorCss(inputSettings?.modalInputTextColor as string),
                            },
                        },
                    },
                },
            },
        };

        modalFormControlTextarea = {
            ...commonWebsitesFormTextareaStyles[inputSettings?.modalInputSize ? inputSettings.modalInputSize : ESize.M],
            '&:hover': {
                borderColor: '#9a9a9a',
            },
            '&:focus': {
                borderColor: '#82f6ff',
                boxShadow: '0 0 0 2px rgb(151 247 255 / 30%);',
                outline: '0',
            },
            borderWidth: '1px',
            boxSizing: 'border-box',
            borderStyle: 'solid',
            borderTopWidth: `${inputSettings?.modalInputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
            borderRightWidth: `${inputSettings?.modalInputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
            borderLeftWidth: `${inputSettings?.modalInputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'}`,
            borderRadius: `${inputSettings?.modalInputBorderRadius}px`,
            background: themeColorCss(inputSettings?.modalInputBackgroundColor as string),
            borderColor: themeColorCss(inputSettings?.modalInputBorderColor as string),
            color: themeColorCss(inputSettings?.modalInputTextColor as string),
            width: '100%',
            '& option': {
                background: themeColorCss(
                    inputSettings?.modalBackgroundColor
                        ? inputSettings?.modalInputBackgroundColor
                        : ('transparent' as string)
                ),
            },
        };
    }

    const formDropdownButton = {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '39px',
        backgroundColor: '#fff',
        border: '1px solid #bcc8c8',
        boxSizing: 'border-box',
        borderRadius: '4px',

        '& div': {
            display: 'flex',
            alignItems: 'center',
        },

        '& span': {
            fontWeight: 'normal',
        },

        '& img': {
            width: '20px',
            height: '20px',
            marginRight: '10px',
        },

        '&:hover': {
            borderColor: '#b8cece',
            backgroundColor: '#fff',
        },

        '& .open': {
            '& .dropdown-toggle.custom-btn': {
                backgroundColor: '#fff',
                borderColor: '#00b175',
                outline: '0 !important',
                boxShadow: '0 0 0 2px rgba(49,193,120,0.2)',
            },

            '& .dropdown-menu': {
                margin: '0 5px',
                maxWidth: '300px',
            },
        },
    };

    const linkControl: JssStyle = {
        ...commonLabelStyles[inputSettings.inputSize],
        color: themeColorCss(inputSettings.linkColor as string),
        marginTop: `${
            inputSettings.inputSize === ESize.S ? '6px' : inputSettings.inputSize === ESize.M ? '8px' : '10px'
        }`,
        display: 'block',
        '&:hover': {
            cursor: 'pointer',
            opacity: 0.8,
        },
    };

    const { position, isSynchronizeStyle } = (formData || {}) as any;
    let formPosition: JssStyle = {
        position: 'relative',
        transform: `rotate(${position?.rotate ?? 0}deg)`,
        zIndex: position?.zIndex,
        '@media (max-width: 992px)': {
            transform: `rotate(${position?.mobileRotate ?? 0}deg)`,
            zIndex: isSynchronizeStyle ? position?.zIndex : position?.mobileZIndex,
        },
    };

    if ('position' in formData) {
        formPosition = {
            position: 'relative',
            transform: `rotate(${formData?.position?.rotate ?? 0}deg)`,
            '@media (max-width: 480px)': {
                transform: `rotate(${formData?.position?.mobileRotate ?? 0}deg)`,
            },
        };

        if (isConstructor) {
            formPosition['&.mobileWidgetViewWrap'] = {
                transform: `rotate(${formData?.position?.mobileRotate ?? 0}deg)`,
            };
            formPosition['&.tabletWidgetViewWrap'] = {
                transform: `rotate(${formData?.position?.mobileRotate ?? 0}deg)`,
            };
        }
    }

    let formFieldsContainer: JssStyle = {};
    let form: JssStyle = {};

    const commonForm: JssStyle = {
        fontFamily: themeFontCss(ThemeFontTags.p, ThemeFontProperties.fontFamily),
        '& label': {
            color: inputSettings.textColor,
        },
        '& .modal-divider': {
            display: 'block',
            width: '100%',
            height: '1px',
            margin: '25px 0',
            background: themeColorCss(ThemeColorNames.primary),
        },
        '& .min-amount-control': {
            '& .input-group': {
                width: '240px',
                position: 'relative',
                display: 'table',
                borderCollapse: 'separate',
                '& input': {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                },
                '& .input-group-addon': {
                    padding: '8px 12px',
                    fontSize: '15px',
                    fontWeight: 400,
                    lineHeight: 1,
                    borderWidth: '1px',
                    boxSizing: 'border-box',
                    borderStyle: 'solid',
                    whiteSpace: 'nowrap',
                    borderTopWidth: `${
                        inputSettings.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'
                    }`,
                    borderRightWidth: `${
                        inputSettings.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'
                    }`,
                    borderLeftWidth: `${
                        inputSettings.inputBorderType === InputBorderEnum.BOTTOM_BORDER ? '0px' : '1px'
                    }`,
                    borderRadius: `${inputSettings.borderRadius}px`,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    background: themeColorCss(inputSettings.backgroundColor as string),
                    borderColor: themeColorCss(inputSettings.borderColor as string),
                    color: themeColorCss(inputSettings.textColor as string),
                    display: 'table-cell',
                    width: '1%',
                    verticalAlign: 'middle',
                    textAlign: 'center',
                },
            },
        },
    };

    const verticalForm: JssStyle = {
        'max-width': '480px',
        margin: `0 ${
            inputSettings.horizontalPosition === EHorizontalPosition.L ||
            inputSettings.horizontalPosition === EHorizontalPosition.M
                ? 'auto'
                : 0
        } 0 ${
            inputSettings.horizontalPosition === EHorizontalPosition.R ||
            inputSettings.horizontalPosition === EHorizontalPosition.M
                ? 'auto'
                : 0
        }`,
    };

    const horizontalForm: JssStyle = {
        display: 'flex',
        justifyContent: horizontalPositionMap[inputSettings.horizontalPosition],
        margin: '-10px',
        '& form': {
            width: '100%',
        },
    };

    let formField: JssStyle = {};

    let modalFormField: JssStyle = {};

    const commonFormField: JssStyle = {
        '&.has-error input': {
            borderColor: '#bb364d',
            backgroundColor: '#fff',
            '&[type=checkbox] + span': {
                color: '#bb364d',
            },
            '&:focus': {
                boxShadow: '0 0 0 2px rgb(212 105 106 / 30%)',
            },
        },
        '&.has-error select': {
            borderColor: '#bb364d',
            backgroundColor: '#fff',
        },
        '& label': {
            ...commonLabelStyles[inputSettings.inputSize],
            fontWeight: 'bold',
            display: 'block',
            marginBottom: `${
                inputSettings.inputSize === ESize.S ? '4px' : inputSettings.inputSize === ESize.M ? '5px' : '8px'
            }`,
            color: themeColorCss(inputSettings.textColor as string),
        },
        '& .checkbox-text': {
            fontWeight: 400,
        },
        '& .radio-text': {
            fontWeight: 400,
        },
        '& .checkbox-input': {
            height: inputSizeMap[inputSettings.inputSize],
            width: inputSizeMap[inputSettings.inputSize],
            position: 'relative',
            top: '1px',
            margin: 0,
        },
        '& .radio-input': {
            height: inputSizeMap[inputSettings.inputSize],
            width: inputSizeMap[inputSettings.inputSize],
            position: 'relative',
            top: '1px',
            margin: 0,
        },
        '& p': {
            color: themeColorCss(inputSettings.textColor as string),
            ...commonLabelStyles[inputSettings.inputSize],
        },

        '& input[type="email"] ': {
            background: themeColorCss(inputSettings.backgroundColor as string),
        },

        '& input[type="text"]': {
            background: themeColorCss(inputSettings.backgroundColor as string),
        },

        '& .ngx-dropzone-file': {
            borderRadius: `${inputSettings.borderRadius}px`,
        },

        '& ngx-dropzone': {
            borderRadius: `${inputSettings.borderRadius}px`,
        },
    };

    const commonModalFormField: JssStyle = {
        '&.has-error input': {
            borderColor: '#bb364d',
            backgroundColor: '#fff',
            '&[type=checkbox] + span': {
                color: '#bb364d',
            },
            '&:focus': {
                boxShadow: '0 0 0 2px rgb(212 105 106 / 30%)',
            },
        },
        '&.has-error select': {
            borderColor: '#bb364d',
            backgroundColor: '#fff',
        },
        '& label': {
            ...commonLabelStyles[inputSettings?.modalInputSize ? inputSettings.modalInputSize : ESize.M],
            fontWeight: 'bold',
            display: 'block',
            marginBottom: `${inputSettings?.modalInputSize === ESize.S ? '4px' : inputSettings?.modalInputSize === ESize.M ? '5px' : '8px'}`,
            color: themeColorCss(inputSettings?.modalTextColor as string),
        },
        '& .checkbox-text': {
            fontWeight: 400,
        },
        '& .radio-text': {
            fontWeight: 400,
        },
        '& .checkbox-input': {
            height: inputSizeMap[inputSettings?.modalInputSize ? inputSettings.modalInputSize : ESize.M],
            width: inputSizeMap[inputSettings?.modalInputSize ? inputSettings.modalInputSize : ESize.M],
            position: 'relative',
            top: '1px',
            margin: 0,
        },
        '& .radio-input': {
            height: inputSizeMap[inputSettings?.modalInputSize ? inputSettings.modalInputSize : ESize.M],
            width: inputSizeMap[inputSettings?.modalInputSize ? inputSettings.modalInputSize : ESize.M],
            position: 'relative',
            top: '1px',
            margin: 0,
        },
        '& p': {
            color: themeColorCss(inputSettings?.modalTextColor as string),
            ...commonLabelStyles[inputSettings?.modalInputSize ? inputSettings.modalInputSize : ESize.M],
        },

        '& input[type="email"] ': {
            background: themeColorCss(inputSettings?.modalInputBackgroundColor as string),
        },

        '& input[type="text"]': {
            background: themeColorCss(inputSettings?.modalInputBackgroundColor as string),
        },

        '& .ngx-dropzone-file': {
            borderRadius: `${inputSettings?.modalInputBorderRadius}px`,
        },

        '& ngx-dropzone': {
            borderRadius: `${inputSettings?.modalInputBorderRadius}px`,
        },
    };

    const verticalFormField: JssStyle = {
        marginBottom: `${
            inputSettings.inputSize === ESize.S ? '12px' : inputSettings.inputSize === ESize.M ? '15px' : '18px'
        }`,
    };

    const verticalModalFormField: JssStyle = {
        marginBottom: `${inputSettings?.modalInputSize === ESize.S ? '12px' : inputSettings?.modalInputSize === ESize.M ? '15px' : '18px'}`,
    };

    const horizontalFormField: JssStyle = {
        margin: '10px',
        minWidth: '100px',
        '@media(min-width: 481px)': {
            maxWidth: '280px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'end',
            '& textarea': {
                height: `${
                    inputSettings.inputSize === ESize.S ? '35px' : inputSettings.inputSize === ESize.M ? '41px' : '57px'
                } !important`,
                resize: 'none',
            },
            '& .iti': {
                marginBottom: '0 !important',
                minWidth: '200px',
            },
        },
        '@media(max-width: 480px)': {
            width: '100%',
        },
    };

    let buttonWrapper: JssStyle = {};
    let formButtonWidth: JssStyle = {};
    let formButtonSize: JssStyle = {};

    let modalButtonWrapper: JssStyle = {};

    if (widget === Widgets.FORM) {
        modalButtonWrapper = {
            display: 'flex',
            marginTop: `${modalFormButtonSettings?.size === ESize.S ? '20px' : modalFormButtonSettings?.size === ESize.M ? '24px' : '28px'}`,
            '&.button-without-form': {
                marginTop: 0,
            },
        };
    }

    if (formStyleType === FormStyleEnum.VERTICAL) {
        form = {
            ...commonForm,
            ...verticalForm,
        };

        formField = {
            ...commonFormField,
            ...verticalFormField,
        };

        if (widget === Widgets.FORM) {
            modalFormField = {
                ...commonModalFormField,
                ...verticalModalFormField,
            };
        }

        buttonWrapper = {
            display: 'flex',
            marginTop: `${formButtonSettings?.size === ESize.S ? '20px' : formButtonSettings?.size === ESize.M ? '24px' : '28px'}`,
            '&.button-without-form': {
                marginTop: 0,
            },
        };
    }

    if (formStyleType === FormStyleEnum.HORIZONTAL) {
        let horizontalFormButtonWrapperWidth: JssStyle = {};

        if (formButtonSettings?.horizontalFormWidthType === EButtonWidthType.full) {
            horizontalFormButtonWrapperWidth = {
                width: '100%',
            };
        } else if (formButtonSettings?.horizontalFormWidthType === EButtonWidthType.fixed) {
            horizontalFormButtonWrapperWidth = {
                width: '100%',
                maxWidth: formButtonSettings?.widthValue + 'px',
            };
        } else {
            horizontalFormButtonWrapperWidth = {
                width: 'auto',
            };
        }

        form = {
            ...commonForm,
            ...horizontalForm,
        };

        formFieldsContainer = {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            justifyContent: horizontalPositionMap[inputSettings.horizontalPosition],
            width: '100%',
        };

        formField = {
            ...commonFormField,
            ...horizontalFormField,
        };

        if (widget === Widgets.FORM) {
            modalFormField = {
                ...commonModalFormField,
                ...verticalModalFormField,
            };
        }

        buttonWrapper = {
            margin: '10px',
            ...horizontalFormButtonWrapperWidth,
            '@media(max-width: 480px)': {
                width: '100%',
            },
        };

        formButtonSize = {
            padding: commonWebsitesFormButtonTextStyles[formButtonSettings?.size || ESize.M].padding,
            fontSize: commonWebsitesFormButtonTextStyles[formButtonSettings?.size || ESize.M].fontSize,
            lineHeight: commonWebsitesFormButtonTextStyles[formButtonSettings?.size || ESize.M].lineHeight,
        };
    }

    if (!isConstructor) {
        const hideOnMobile = (formData as any)?.hideOnMobile;
        const hideOnTablet = (formData as any)?.hideOnTablet;
        const hideOnDesktop = (formData as any)?.hideOnDesktop;

        if (hideOnMobile) {
            form['@media (max-width: 480px)'] = { display: 'none' };
        }

        if (hideOnTablet) {
            form['@media (min-width: 481px) and (max-width: 992px)'] = { display: 'none' };
        }

        if (hideOnDesktop) {
            form['@media (min-width: 993px)'] = { display: 'none' };
        }
    }

    const pointerEvents: JssStyle = {
        pointerEvents: 'none',
    };

    if (formStyleType === FormStyleEnum.HORIZONTAL) {
        if (formButtonSettings?.horizontalFormWidthType === EButtonWidthType.auto) {
            formButtonWidth = {
                width: 'auto',
            };
        } else if (formButtonSettings?.horizontalFormWidthType === EButtonWidthType.fixed) {
            formButtonWidth = {
                width: '100%',
                maxWidth: formButtonSettings?.widthValue + 'px',
            };
        } else {
            formButtonWidth = {
                width: '100%',
            };
        }
    } else {
        if (formButtonSettings?.widthType === EButtonWidthType.auto) {
            formButtonWidth = {
                width: 'auto',
            };
        } else if (formButtonSettings?.widthType === EButtonWidthType.fixed) {
            formButtonWidth = {
                width: '100%',
                maxWidth: formButtonSettings?.widthValue + 'px',
            };
        } else {
            formButtonWidth = {
                width: '100%',
            };
        }
    }

    let buttonStyles: JssStyle = {
        // textAlign: 'center',
        // display: 'block !important',
        boxSizing: 'border-box',
        margin: `0 ${
            inputSettings.horizontalPosition === EHorizontalPosition.L ||
            inputSettings.horizontalPosition === EHorizontalPosition.M
                ? 'auto'
                : 0
        } 0 ${
            inputSettings.horizontalPosition === EHorizontalPosition.R ||
            inputSettings.horizontalPosition === EHorizontalPosition.M
                ? 'auto'
                : 0
        } !important`,
        flexDirection: formButtonSettings?.buttonIcon?.position === EHorizontalPosition.L ? 'row' : 'row-reverse',
        ...formButtonWidth,
        ...formButtonSize,
    };

    if (formButtonSettings?.styleType === EButtonStyleType.custom) {
        buttonStyles = {
            ...buttonStyles,
            ...getBaseButtonData({
                buttonBase: formButtonSettings?.buttonBase,
                buttonHover: formButtonSettings?.buttonHover,
                advancedRadius: formButtonSettings?.advancedRadius,
                advancedBorderSettings: formButtonSettings?.advancedBorderSettings,
            }),
        };
    }

    if (formButtonSettings?.isShadow) {
        const s = formButtonSettings?.shadowSettings ?? DEFAULT_SHADOW_SETTINGS;
        buttonStyles = {
            ...buttonStyles,
            boxShadow: `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.spread}px ${s.color}`,
        };
    }

    let modalButtonStyles: JssStyle = {};

    if (widget === Widgets.FORM) {
        modalButtonStyles = {
            width: '100%',
            margin: '0 !important',
        };

        if (modalFormButtonSettings?.styleType === EButtonStyleType.custom) {
            modalButtonStyles = {
                ...buttonStyles,
                ...getBaseButtonData({
                    buttonBase: modalFormButtonSettings?.buttonBase,
                    buttonHover: modalFormButtonSettings?.buttonHover,
                    advancedRadius: modalFormButtonSettings?.advancedRadius,
                    advancedBorderSettings: modalFormButtonSettings?.advancedBorderSettings,
                }),
            };
        }

        if (modalFormButtonSettings?.isShadow) {
            const s = modalFormButtonSettings?.shadowSettings ?? DEFAULT_SHADOW_SETTINGS;
            modalButtonStyles = {
                ...modalButtonStyles,
                boxShadow: `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.spread}px ${s.color}`,
            };
        }
    }

    const paymentFormDescription: JssStyle = {
        '& h2': {
            color: themeColorCss(inputSettings.textColor as string),
        },
        '& p': {
            color: themeColorCss(inputSettings.textColor as string),
        },
        color: themeColorCss(inputSettings.textColor as string),
    };

    const passwordIconStyle: JssStyle = {
        height: passwordIconMap[inputSettings.inputSize],
        width: passwordIconMap[inputSettings.inputSize],
        top: 'calc(50% - ' + passwordIconMap[inputSettings.inputSize] / 2 + 'px)',
        '& svg': {
            height: passwordIconMap[inputSettings.inputSize],
            width: passwordIconMap[inputSettings.inputSize],
            color: inputSettings.inputTextColor,
        },
    };

    const modalStyles = {} as any;
    if (widget === Widgets.PAYMENT_FORM || widget === Widgets.LOGIN_EDU || widget === Widgets.FORM) {
        const modalBackgroundColor = inputSettings?.modalBackgroundColor ?? '#fbfcfc';
        const modalBorderRadius = inputSettings?.modalBorderRadius ?? 6;
        const paymentModalPadding = inputSettings?.modalPadding ?? 45;
        const eduModalPadding = inputSettings?.modalPadding ?? 30;

        const modalPadding = inputSettings?.modalPadding ?? 45;

        modalStyles.modalBodyStyles = {
            '&.payment-form-modal-container': {
                background: themeColorCss(modalBackgroundColor),
                borderRadius: `${modalBorderRadius}px`,
                padding: `${paymentModalPadding}px`,
                '& .close-modal-btn': {
                    color: themeColorCss(inputSettings.textColor as string),
                },
            },
            '&.form-modal-container': {
                background: themeColorCss(modalBackgroundColor),
                borderRadius: `${modalBorderRadius}px`,
                padding: `${modalPadding}px`,
                '& .close-modal-btn': {
                    color: themeColorCss(inputSettings?.modalTitleTextColor as string),
                },
                '& h2': {
                    color: themeColorCss(inputSettings?.modalTitleTextColor as string),
                },
            },
            '&.edu-modal': {
                background: themeColorCss(modalBackgroundColor),
                borderRadius: `${modalBorderRadius}px`,
                padding: `${eduModalPadding}px`,
                '& h2': {
                    color: themeColorCss(inputSettings.textColor as string),
                },
                '& .close-modal-btn': {
                    color: themeColorCss(inputSettings.textColor as string),
                },
                '& .sp-ui-button-secondary-edu': {
                    color: themeColorCss(inputSettings.linkColor as string),
                    border: '1px dashed',
                    borderColor: themeColorCss(inputSettings.linkColor as string),
                    '&:hover': {
                        background: 'none',
                        color: themeColorCss(inputSettings.linkColor as string),
                        border: '1px dashed',
                        borderColor: themeColorCss(inputSettings.linkColor as string),
                        boxShadow: 'none',
                        opacity: 0.8,
                    },
                },
                '& .password-icon': {
                    '& svg': {
                        color: themeColorCss(inputSettings.inputTextColor as string),
                    },
                },
            },
        };

        modalStyles.modalContainerStyles = {
            '& .modal-content': {
                borderRadius: `${modalBorderRadius + 2}px`,
            },
        };
    }

    let chatBotWrapper: any = {
        display: 'flex',
        alignItems: 'center',
    };

    if (isSubscriptionForm && chatBotsButtonSettings) {
        if (formStyleType === FormStyleEnum.VERTICAL) {
            if (chatBotsButtonSettings.type === EButtonType.icon) {
                chatBotWrapper = {
                    ...chatBotWrapper,
                    flexWrap: 'wrap',
                    gap: '4px',
                    '& a': {
                        flexDirection: 'column',
                        width: '100%',
                        margin: 0,
                        flex: '1 1 0',
                        ...getChatBotIconButtonSizeStyles(chatBotsButtonSettings.buttonSize),
                    },
                };
            }

            if (chatBotsButtonSettings.type === EButtonType.button) {
                chatBotWrapper = {
                    ...chatBotWrapper,
                    flexWrap: 'wrap',
                    gap: '10px',
                    '& a': {
                        width: '100%',
                        margin: 0,
                    },
                };
            }
        }

        if (formStyleType === FormStyleEnum.HORIZONTAL) {
            if (chatBotsButtonSettings.type === EButtonType.icon) {
                chatBotWrapper = {
                    ...chatBotWrapper,
                    margin: '10px',
                    width: '100%',
                    flexWrap: 'wrap',
                    gap: '10px',
                    justifyContent: horizontalPositionMap[inputSettings.horizontalPosition],
                    '& a': {
                        flexDirection: 'column',
                        width: 'auto',
                        margin: 0,
                        ...getChatBotIconButtonSizeStyles(chatBotsButtonSettings.buttonSize),
                    },
                };
            }

            if (chatBotsButtonSettings.type === EButtonType.button) {
                chatBotWrapper = {
                    ...chatBotWrapper,
                    flexWrap: 'wrap',
                    margin: '10px',
                    width: '100%',
                    gap: '10px',
                    justifyContent: horizontalPositionMap[inputSettings.horizontalPosition],
                    '& a': {
                        width: 'auto',
                        margin: 0,
                    },
                };
            }
        }
    }

    let returnedData = {
        form,
        formPosition,
        formFieldsContainer,
        formField,
        modalFormField,
        formControl,
        formControlTextarea,
        modalFormControl,
        modalFormControlTextarea,
        pointerEvents,
        buttonWrapper,
        modalButtonWrapper,
        buttonStyles,
        modalButtonStyles,
        paymentFormDescription,
        linkControl,
        passwordIconStyle,
        formDropdownButton,
        ...modalStyles,
    };

    if (isSubscriptionForm && chatBotsButtonSettings) {
        returnedData = {
            ...returnedData,
            chatBotWrapper,
            chatBotGroup: getButtonStyles(chatBotsButtonSettings, 13 as number),
        };
    }

    return returnedData;
}
